// src/theme.js

import { createTheme } from '@mui/material/styles';

/* -------------------------------------------------------------------------- */
/*  BRAND PALETTE                                                             */
/* -------------------------------------------------------------------------- */
const palette = {
    blue: {
        50: '#E6EFF9',
        100: '#C7DAF3',
        200: '#96B6E7',
        300: '#6593DB',
        400: '#356ECF',
        500: '#002F6C', // SC DPH navy (primary)
        600: '#002858',
        700: '#001E43',
        800: '#00142E',
        900: '#000A19',
    },
    green: {
        50: '#F0F7EB',
        100: '#DAECB9',
        200: '#B4DA6F',
        300: '#8CC63F',
        400: '#74AD2A',
        500: '#78BE20', // SC DPH green (secondary)
        600: '#63901A',
        700: '#4F660F',
        800: '#3A3D08',
        900: '#1F1F03',
    },
    orange: {
        50: '#FFFBE6',
        100: '#FFF3B3',
        200: '#FFE680',
        300: '#FFD94D',
        400: '#FFCB1A',
        500: '#FFD200', // SC DPH gold (accent)
        600: '#CCAA00',
        700: '#998200',
        800: '#665900',
        900: '#332D00',
    },
    neutral: {
        0: '#FFFFFF',
        50: '#F8F9FA',
        100: '#F1F3F5',
        200: '#E9ECEF',
        300: '#DEE2E6',
        400: '#CED4DA',
        500: '#ADB5BD',
        600: '#6C757D',
        700: '#495057',
        800: '#343A40',
        900: '#212529',
        1000: '#000000',
    },
};

/* -------------------------------------------------------------------------- */
/*  CUSTOM TOKENS                                                             */
/* -------------------------------------------------------------------------- */
const colors = {
    primary: palette.blue[500],
    secondary: palette.green[500],
    accent: palette.orange[500],
    success: palette.green[400],
    warning: palette.orange[400],
    danger: '#E63946',
    info: palette.blue[400],
    blue: palette.blue,
    green: palette.green,
    orange: palette.orange,
    neutral: palette.neutral,
};

const gradients = {
    gradientHero: `linear-gradient(135deg, ${palette.blue[500]} 0%, ${palette.green[500]} 100%)`,
    gradientCta: `linear-gradient(135deg, ${palette.orange[500]} 0%, ${palette.blue[500]} 100%)`,
    gradientSurface: `linear-gradient(180deg, ${palette.neutral[0]} 0%, ${palette.neutral[50]} 100%)`,
};

const fonts = {
    fontHeading: "'Cabin', sans-serif",
    fontBody: "'Roboto Condensed', sans-serif",
    fontLogoWord: "'Red Hat Text', sans-serif",
    fontLogoTagline: "'DIN Pro Condensed', sans-serif",
    fontMonospace: "'JetBrains Mono', monospace",
};

const fontSizes = {
    fontSizeExtraSmall: '0.75rem',
    fontSizeSmall: '0.875rem',
    fontSizeMedium: '1rem',
    fontSizeLarge: '1.125rem',
    fontSizeExtraLarge: '1.25rem',
    fontSize2XL: '1.5rem',
    fontSize3XL: '1.875rem',
    fontSize4XL: '2.25rem',
    fontSize5XL: '3rem',
    fontSize6XL: '3.75rem',
};

const lineHeights = {
    lineHeightTight: 1.1,
    lineHeightSnug: 1.3,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.7,
};

const headings = {
    h1: {
        fontSize: fontSizes.fontSize5XL,
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
    },
    h2: {
        fontSize: fontSizes.fontSize4XL,
        fontWeight: 700,
        lineHeight: 1.2,
    },
    h3: {
        fontSize: fontSizes.fontSize3XL,
        fontWeight: 700,
        lineHeight: 1.25,
    },
    h4: {
        fontSize: fontSizes.fontSize2XL,
        fontWeight: 600,
        lineHeight: 1.3,
    },
    h5: {
        fontSize: fontSizes.fontSizeExtraLarge,
        fontWeight: 600,
        lineHeight: 1.35,
    },
    h6: {
        fontSize: fontSizes.fontSizeMedium,
        fontWeight: 600,
        lineHeight: 1.4,
    },
};

const spacingScale = {
    spacing0: '0',
    spacing1: '0.25rem',
    spacing2: '0.5rem',
    spacing3: '0.75rem',
    spacing4: '1rem',
    spacing5: '1.25rem',
    spacing6: '1.5rem',
    spacing8: '2rem',
    spacing10: '2.5rem',
    spacing12: '3rem',
    spacing16: '4rem',
    spacing20: '5rem',
    spacing24: '6rem',
};

const radii = {
    radiusNone: '0',
    radiusSmall: '4px',
    radiusMedium: '8px',
    radiusLarge: '12px',
    radiusXL: '24px',
    radiusFull: '9999px',
};

const shadows = [
    '0 1px 2px rgba(0,0,0,0.05)',                                         // extra-small
    '0 1px 3px rgba(0,0,0,0.1),0 1px 2px rgba(0,0,0,0.06)',              // small
    '0 4px 6px rgba(0,0,0,0.1),0 2px 4px rgba(0,0,0,0.06)',              // medium
    '0 10px 15px rgba(0,0,0,0.1),0 4px 6px rgba(0,0,0,0.1)',             // large
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',           // extra-large
    // fill out to 25 entries for MUI’s shadow index (repeat last as necessary)
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.1),0 10px 10px rgba(0,0,0,0.04)'
];

const breakpointsValues = {
    xs: 320,
    sm: 480,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536,
};

const zIndexValues = {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    overlay: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
};

const transitionDurations = {
    durationQuick: 150,
    durationNormal: 300,
    durationSlow: 500,
};

const transitionEasings = {
    easingStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easingDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    easingAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
};

const containerSizes = {
    containerSmall: '640px',
    containerMedium: '768px',
    containerLarge: '1024px',
    containerXL: '1280px',
    container2XL: '1536px',
};

const utils = {
    convertPixelsToRem: function (pixels) {
        return `${pixels / 16}rem`;
    },
    convertHexToRgba: function (hex, alpha = 1) {
        const sanitized = hex.replace('#', '');
        const integer = parseInt(sanitized, 16);
        const red = (integer >> 16) & 255;
        const green = (integer >> 8) & 255;
        const blue = integer & 255;
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }
};

/* -------------------------------------------------------------------------- */
/*  BUILD MUI THEME                                                           */
/* -------------------------------------------------------------------------- */
const muiTheme = createTheme({
    palette: {
        primary: { light: palette.blue[300], main: palette.blue[500], dark: palette.blue[700], contrastText: palette.neutral[0] },
        secondary: { light: palette.green[300], main: palette.green[500], dark: palette.green[700], contrastText: palette.neutral[0] },
        error: { main: colors.danger, contrastText: palette.neutral[0] },
        warning: { main: colors.warning, contrastText: palette.neutral[900] },
        info: { main: colors.info, contrastText: palette.neutral[0] },
        success: { main: colors.success, contrastText: palette.neutral[0] },
        grey: { ...palette.neutral },
        background: {
            default: palette.neutral[50],
            paper: palette.neutral[0]
        },
        text: {
            primary: palette.neutral[800],
            secondary: palette.neutral[600],
            disabled: palette.neutral[400]
        }
    },

    typography: {
        fontFamily: fonts.fontBody,
        h1: { ...headings.h1, fontFamily: fonts.fontHeading },
        h2: { ...headings.h2, fontFamily: fonts.fontHeading },
        h3: { ...headings.h3, fontFamily: fonts.fontHeading },
        h4: { ...headings.h4, fontFamily: fonts.fontHeading },
        h5: { ...headings.h5, fontFamily: fonts.fontHeading },
        h6: { ...headings.h6, fontFamily: fonts.fontHeading },
        button: { textTransform: 'none', fontWeight: 600 },
        subtitle1: { fontSize: fontSizes.fontSizeLarge },
        subtitle2: { fontSize: fontSizes.fontSizeMedium },
        body1: { fontSize: fontSizes.fontSizeMedium },
        body2: { fontSize: fontSizes.fontSizeSmall },
        caption: { fontSize: fontSizes.fontSizeExtraSmall }
    },

    breakpoints: { values: breakpointsValues },

    shape: { borderRadius: parseInt(radii.radiusMedium, 10) },

    shadows: shadows,

    spacing: factor => {
        const key = `spacing${factor}`;
        return spacingScale[key] || factor;
    },

    transitions: {
        duration: transitionDurations,
        easing: transitionEasings,
    },

    zIndex: zIndexValues,
});

/* -------------------------------------------------------------------------- */
/*  ATTACH CUSTOM TOKENS                                                      */
/* -------------------------------------------------------------------------- */
muiTheme.customColors = colors;
muiTheme.customGradients = gradients;
muiTheme.customFonts = fonts;
muiTheme.customFontSizes = fontSizes;
muiTheme.customLineHeights = lineHeights;
muiTheme.customHeadings = headings;
muiTheme.customRadii = radii;
muiTheme.customSpacingScale = spacingScale;
muiTheme.customContainer = containerSizes;
muiTheme.customUtils = utils;

export default muiTheme;
